<template>
  <div class="member-detail">
    <el-card v-loading="loading">
      <!-- 会员头部 -->
      <div class="member-header">
        <div class="member-photo">
          <el-image
            :src="member.photo || '/static/images/default-avatar.png'"
            fit="cover"
          >
            <template #error>
              <div class="image-slot">
                <el-icon><User /></el-icon>
              </div>
            </template>
          </el-image>
        </div>
        
        <div class="member-basic">
          <h1>{{ member.real_name }}</h1>
          <div class="member-tags">
            <el-tag type="success">注册会员</el-tag>
            <el-tag v-if="member.title">{{ member.title }}</el-tag>
          </div>
        </div>
      </div>

      <!-- 会员信息 -->
      <el-descriptions :column="2" border class="member-info">
        <el-descriptions-item label="性别">
          {{ member.gender === 1 ? '男' : member.gender === 2 ? '女' : '未知' }}
        </el-descriptions-item>
        
        <el-descriptions-item label="出生日期">
          {{ member.birthday }}
        </el-descriptions-item>
        
        <el-descriptions-item label="任职单位">
          {{ member.company }}
        </el-descriptions-item>
        
        <el-descriptions-item label="职称">
          {{ member.title }}
        </el-descriptions-item>
        
        <el-descriptions-item label="学历">
          {{ member.education }}
        </el-descriptions-item>
        
        <el-descriptions-item label="毕业院校">
          {{ member.graduate_school }}
        </el-descriptions-item>
        
        <el-descriptions-item label="专业">
          {{ member.major }}
        </el-descriptions-item>
        
        <el-descriptions-item label="从业时间">
          {{ member.start_date }}
        </el-descriptions-item>
        
        <el-descriptions-item label="联系方式" :span="2">
          <el-icon><Phone /></el-icon>
          {{ member.phone }}
        </el-descriptions-item>
        
        <el-descriptions-item label="电子邮箱" :span="2">
          <el-icon><Message /></el-icon>
          {{ member.email }}
        </el-descriptions-item>
      </el-descriptions>

      <!-- 个人简介 -->
      <div class="member-section" v-if="member.introduction">
        <h2>个人简介</h2>
        <div class="section-content">
          {{ member.introduction }}
        </div>
      </div>

      <!-- 专业领域 -->
      <div class="member-section" v-if="member.expertise">
        <h2>专业领域</h2>
        <div class="section-content">
          <el-tag
            v-for="(item, index) in member.expertise.split(',')"
            :key="index"
            class="expertise-tag"
          >
            {{ item }}
          </el-tag>
        </div>
      </div>

      <!-- 工作经历 -->
      <div class="member-section" v-if="member.experiences && member.experiences.length > 0">
        <h2>见习/实习经历</h2>
        <el-timeline>
          <el-timeline-item
            v-for="(exp, index) in member.experiences"
            :key="index"
            :timestamp="exp.time ? exp.time.join(' 至 ') : ''"
            placement="top"
          >
            <el-card>
              <h4>{{ exp.location }}</h4>
              <p>督导老师：{{ exp.supervisorName }}</p>
              <p>督导时长：{{ exp.supervisorHours }}小时</p>
            </el-card>
          </el-timeline-item>
        </el-timeline>
      </div>

      <!-- 资格证书 -->
      <div class="member-section" v-if="member.certificate">
        <h2>资格证书</h2>
        <div class="certificate-image">
          <el-image
            :src="member.certificate"
            fit="contain"
            :preview-src-list="[member.certificate]"
          />
        </div>
      </div>

      <!-- 返回按钮 -->
      <div class="member-footer">
        <el-button @click="goBack">返回列表</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import memberApi from '@/api/member'
import { User, Phone, Message } from '@element-plus/icons-vue'

export default {
  name: 'MemberDetail',
  
  components: {
    User,
    Phone,
    Message
  },
  
  data() {
    return {
      member: {
        experiences: []
      },
      loading: false
    }
  },
  
  mounted() {
    this.loadMemberDetail()
  },
  
  methods: {
    /**
     * 加载会员详情
     */
    async loadMemberDetail() {
      const memberId = this.$route.params.id
      
      if (!memberId) {
        this.$message.error('会员ID不存在')
        this.$router.back()
        return
      }
      
      this.loading = true
      
      try {
        const res = await memberApi.getMemberDetail(memberId)
        
        if (res && res.code === 2000) {
          this.member = res.data
        }
      } catch (error) {
        console.error('加载会员详情失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 返回列表
     */
    goBack() {
      this.$router.push('/member/list')
    }
  }
}
</script>

<style lang="scss" scoped>
.member-detail {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .el-card {
    max-width: 1000px;
    margin: 0 auto;
  }
  
  .member-header {
    display: flex;
    align-items: center;
    gap: 30px;
    padding: 20px 0;
    border-bottom: 1px solid #e4e7ed;
    margin-bottom: 30px;
    
    .member-photo {
      width: 150px;
      height: 150px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      
      .el-image {
        width: 100%;
        height: 100%;
      }
      
      .image-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: #f5f7fa;
        color: #909399;
        font-size: 50px;
      }
    }
    
    .member-basic {
      flex: 1;
      
      h1 {
        margin: 0 0 15px 0;
        font-size: 28px;
        color: #303133;
      }
      
      .member-tags {
        display: flex;
        gap: 10px;
      }
    }
  }
  
  .member-info {
    margin-bottom: 30px;
    
    .el-icon {
      margin-right: 5px;
      color: #409eff;
    }
  }
  
  .member-section {
    margin-top: 30px;
    
    h2 {
      margin: 0 0 20px 0;
      font-size: 20px;
      color: #303133;
      padding-bottom: 10px;
      border-bottom: 2px solid #409eff;
    }
    
    .section-content {
      line-height: 1.8;
      color: #606266;
      
      .expertise-tag {
        margin-right: 10px;
        margin-bottom: 10px;
      }
    }
    
    .certificate-image {
      max-width: 500px;
      
      .el-image {
        width: 100%;
        cursor: pointer;
      }
    }
  }
  
  .member-footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid #e4e7ed;
    text-align: center;
  }
}

@media (max-width: 768px) {
  .member-detail {
    padding: 10px;
    
    .member-header {
      flex-direction: column;
      text-align: center;
      
      .member-photo {
        width: 120px;
        height: 120px;
      }
      
      .member-basic {
        h1 {
          font-size: 22px;
        }
        
        .member-tags {
          justify-content: center;
        }
      }
    }
  }
}
</style>
